// 일봉 PIT 백테스트: 관측된 정보로 신호를 정하고 이후 시가·종가에만 체결
// 가격은 수정연결(차감식) 기준. 국채선물 1.00pt = 1,000,000원/계약 (액면 1억, 1틱 0.01 = 1만원)

export const KRW_PER_PT = 1_000_000;
const PRICE_EPSILON = 1e-9; // 가격 단위보다 작은 부동소수점 누적 오차는 동일 가격으로 취급

export type Series = {
  d: string[]; o: number[]; h: number[]; l: number[]; c: number[];
  fill: number[]; raw: number[]; vw: number[]; roll: number[];
};

export type PriceRule = "open" | "high" | "low" | "close" | "full";
export type FillModel = "open" | "close" | "openClose";
export type FillSource = "open" | "close" | "mixed";
export const FILL_LABEL: Record<FillSource, string> = {
  open: "시가", close: "종가", mixed: "복합 체결",
};

export type Params = {
  maLen: number;
  strategy?: "ma" | "prevClose";
  dailyLots: number;           // 하루 매수·매도 합계 최대 주문 수량
  timing: "prev" | "open";     // prev: 전일 확정 → 당일 시가/종가, open: 당일 시가 확인 → 당일 종가
  buyFill: FillModel;
  sellFill: FillModel;
  buyRule: PriceRule;           // 선택 가격 > MA. full은 기존 저가 조건의 별칭
  sellRule: PriceRule;          // 선택 가격 < MA. full은 기존 고가 조건의 별칭
  mode: "ls" | "long";          // 롱숏 / 롱 온리
  capDays: number;              // 최대 보유 = 일일수량 × capDays (0 = 무제한)
  tp: boolean;                  // 이격도 변곡 익실현
  tpPriority: "ma" | "tp";      // 신호 충돌 시 우선
  slipTicks: number;
  feePerLot: number;            // 원/계약
  start: string;
  end: string;
};

export type Action = "B" | "S" | "BS" | "TP" | null;

export type Execution = {
  phase: "open" | "close"; side: "B" | "S"; quantity: number; price: number;
  source: FillSource; reference: number | null; buyPrice: number; sellPrice: number;
  reason: "signal" | "tp";
};

export type Row = {
  i: number; d: string; o: number; h: number; l: number; c: number; pc: number; ma: number | null;
  disp: number | null; sig: number; action: Action; q: number; buys: number; sells: number;
  fill: number; fillSrc: FillSource; roll: number;
  signalDate: string; signalPhase: "open" | "close"; referenceDate: string; signalRef: number | null; signalBuy: number; signalSell: number;
  executions: Execution[];
  pos: number; pnl: number; cost: number; cum: number; dd: number;
};

export function sma(arr: number[], n: number): (number | null)[] {
  const out: (number | null)[] = new Array(arr.length).fill(null);
  let s = 0;
  for (let i = 0; i < arr.length; i++) {
    s += arr[i];
    if (i >= n) s -= arr[i - n];
    if (i >= n - 1) out[i] = s / n;
  }
  return out;
}

function rulePrice(rule: PriceRule, side: "buy" | "sell", o: number, h: number, l: number, c: number) {
  if (rule === "full") return side === "buy" ? l : h;
  return rule === "open" ? o : rule === "high" ? h : rule === "low" ? l : c;
}

function classify(buy: number, sell: number, reference: number | null) {
  if (reference == null) return 0;
  const up = buy - reference > PRICE_EPSILON, down = reference - sell > PRICE_EPSILON;
  return up === down ? 0 : up ? 1 : -1; // 동시 충족 또는 동일 가격은 관망
}

// 이격도(종가/MA×100)가 i일 종가에서 꺾였는지: +1 고점 꺾임(롱 익실현), -1 저점 반등(숏 익실현)
export function turnOf(a: number | null | undefined, b: number | null | undefined, c: number | null | undefined) {
  if (a == null || b == null || c == null) return 0;
  if (b > 100 && b >= a && c < b) return 1;
  if (b < 100 && b <= a && c > b) return -1;
  return 0;
}

function dispTurn(disp: (number | null)[], i: number) {
  return turnOf(disp[i - 2], disp[i - 1], disp[i]);
}

function orderWindows(buy: FillModel, sell: FillModel, n: number) {
  const quantities = (fill: FillModel) => fill === "open" ? [n, 0] : fill === "close" ? [0, n]
    : [Math.floor(n / 2), Math.ceil(n / 2)];
  const buys = quantities(buy), sells = quantities(sell);
  return (["open", "close"] as const).map((phase, i) => ({ phase, buy: buys[i], sell: sells[i] }))
    .filter((window) => window.buy + window.sell > 0);
}

export function run(D: Series, p: Params) {
  if (!Number.isInteger(p.maLen) || p.maLen < 1) throw new Error("이평 기간은 양의 정수여야 합니다.");
  if (!Number.isSafeInteger(p.dailyLots) || p.dailyLots < 1) throw new Error("일일 주문 수량은 양의 정수여야 합니다.");
  if (p.timing !== "prev" && p.timing !== "open") throw new Error("신호 시점은 전일 확정 또는 당일 시가만 지원합니다.");
  const supportedFills: FillModel[] = ["open", "close", "openClose"];
  if (!supportedFills.includes(p.buyFill) || !supportedFills.includes(p.sellFill)) {
    throw new Error("일봉 체결은 시가·종가만 지원합니다.");
  }
  if (p.timing === "open" && (p.buyFill !== "close" || p.sellFill !== "close")) {
    throw new Error("당일 시가를 확인한 신호는 이후 종가에만 체결할 수 있습니다.");
  }
  const strategy = p.strategy ?? "ma";
  const atOpen = p.timing === "open";
  const ma = sma(D.c, p.maLen);
  const disp = D.c.map((c, i) => (ma[i] == null ? null : (c / ma[i]!) * 100));
  const lotsPerDay = p.dailyLots;
  const cap = p.capDays > 0 ? lotsPerDay * p.capDays : Infinity;
  const costPerLot = p.slipTicks * 0.01 * KRW_PER_PT + p.feePerLot;
  const windows = orderWindows(p.buyFill, p.sellFill, lotsPerDay);
  const rows: Row[] = [];
  let pos = 0, cum = 0, peak = 0, tpSide = 0;
  // 기존 MA 백테스트의 준비 기간은 유지. 전일 종가 전략은 MA 준비를 기다리지 않는다.
  const warmup = strategy === "prevClose" ? (atOpen ? 1 : 2) : p.maLen + 2;
  for (let i = warmup; i < D.d.length; i++) {
    if (D.d[i] < p.start || D.d[i] > p.end) continue;
    const sourceIndex = atOpen ? i : i - 1;
    const signalDate = D.d[sourceIndex];
    const signalPhase = atOpen ? "open" : "close";
    const referenceIndex = strategy === "prevClose" ? (atOpen ? i - 1 : i - 2) : i - 1;
    const referenceDate = D.d[referenceIndex];
    const signalRef = strategy === "prevClose" ? D.c[referenceIndex] : ma[referenceIndex];
    const signalBuy = atOpen ? D.o[i] : strategy === "prevClose" ? D.c[i - 1]
      : rulePrice(p.buyRule, "buy", D.o[i - 1], D.h[i - 1], D.l[i - 1], D.c[i - 1]);
    const signalSell = atOpen || strategy === "prevClose" ? signalBuy
      : rulePrice(p.sellRule, "sell", D.o[i - 1], D.h[i - 1], D.l[i - 1], D.c[i - 1]);
    const difference = signalRef == null ? 0 : signalBuy - signalRef;
    const sig = strategy === "prevClose" ? (difference < -PRICE_EPSILON ? 1 : difference > PRICE_EPSILON ? -1 : 0)
      : classify(signalBuy, signalSell, signalRef);
    if (p.tp) {
      const turn = dispTurn(disp, i - 1);
      if (turn === 1 && pos > 0) tpSide = 1;
      if (turn === -1 && pos < 0) tpSide = -1;
      if ((tpSide === 1 && pos <= 0) || (tpSide === -1 && pos >= 0)) tpSide = 0;
    }
    let q = 0, buys = 0, sells = 0, trade = 0, tpUsed = false, notional = 0;
    const executions: Execution[] = [];
    for (const window of windows) {
      let usedBuy = 0, usedSell = 0;
      while (buys + sells < lotsPerDay) {
        let dir = sig;
        let isTp = false;
        const cur = pos + q;
        if (tpSide !== 0 && (dir === 0 || (p.tpPriority === "tp" && dir === tpSide))) {
          dir = cur * tpSide > 0 ? -tpSide : 0;
          isTp = dir !== 0;
        }
        if (!dir || (p.mode === "long" && dir < 0 && cur <= 0) || Math.abs(cur + dir) > cap) break;
        if (dir > 0 ? usedBuy >= window.buy : usedSell >= window.sell) break;
        const source: FillSource = window.phase;
        const px = source === "open" ? D.o[i] : D.c[i];
        q += dir;
        if (dir > 0) { buys++; usedBuy++; } else { sells++; usedSell++; }
        trade += dir * (D.c[i] - px) * KRW_PER_PT;
        notional += px;
        tpUsed ||= isTp;
        const side = dir > 0 ? "B" : "S";
        const last = executions[executions.length - 1];
        if (last && last.phase === window.phase && last.side === side && last.reason === (isTp ? "tp" : "signal")) last.quantity++;
        else executions.push({ phase: window.phase, side, quantity: 1, price: px, source,
          reference: signalRef, buyPrice: signalBuy, sellPrice: signalSell, reason: isTp ? "tp" : "signal" });
        if (tpSide !== 0 && (pos + q) * tpSide <= 0) {
          tpSide = 0;
          if (isTp) break; // 같은 주문 묶음에서 청산 직후 다시 진입하지 않는다
        }
      }
    }
    const n = buys + sells;
    const action: Action = !n ? null : buys && sells ? "BS" : tpUsed ? "TP" : buys ? "B" : "S";
    const fill = n ? notional / n : D.c[i];
    const sources = new Set(executions.map((e) => e.source));
    const fillSrc: FillSource = sources.size > 1 ? "mixed" : executions[0]?.source ?? "close";
    const carry = pos * (D.c[i] - D.c[i - 1]) * KRW_PER_PT;
    const cost = n * costPerLot;
    const pnl = carry + trade - cost;
    pos += q; cum += pnl; peak = Math.max(peak, cum);
    rows.push({
      i, d: D.d[i], o: D.o[i], h: D.h[i], l: D.l[i], c: D.c[i], pc: D.c[i - 1], ma: ma[i],
      disp: disp[i], sig, action, q, buys, sells, fill, fillSrc, roll: D.roll[i],
      signalDate, signalPhase, referenceDate, signalRef, signalBuy, signalSell, executions,
      pos, pnl, cost, cum, dd: cum - peak,
    });
  }
  return { rows, ma, lotsPerDay, cap, stats: stats(rows), years: byYear(rows) };
}

export type Stats = NonNullable<ReturnType<typeof stats>>;

function stats(rows: Row[]) {
  const n = rows.length;
  if (!n) return null;
  const mean = rows.reduce((a, r) => a + r.pnl, 0) / n;
  const sd = Math.sqrt(rows.reduce((a, r) => a + (r.pnl - mean) ** 2, 0) / Math.max(1, n - 1));
  const years = n / 252;
  const total = rows[n - 1].cum;
  const mdd = Math.min(0, ...rows.map((r) => r.dd));
  const exposed = rows.filter((r, k) => (k ? rows[k - 1].pos : 0) !== 0 || r.buys + r.sells > 0);
  return {
    days: n, from: rows[0].d, to: rows[n - 1].d, years, total, perYear: total / years, mdd,
    calmar: mdd < 0 ? total / years / -mdd : null,
    sharpe: sd > 0 ? (mean / sd) * Math.sqrt(252) : null,
    hit: exposed.length ? exposed.filter((r) => r.pnl > 0).length / exposed.length : null,
    buyDays: rows.filter((r) => r.buys > 0).length,
    sellDays: rows.filter((r) => r.sells > 0).length,
    tpDays: rows.filter((r) => r.executions.some((e) => e.reason === "tp")).length,
    lots: rows.reduce((a, r) => a + r.buys + r.sells, 0),
    cost: rows.reduce((a, r) => a + r.cost, 0),
    maxLong: Math.max(0, ...rows.map((r) => r.pos)),
    maxShort: Math.min(0, ...rows.map((r) => r.pos)),
    avgAbsPos: rows.reduce((a, r) => a + Math.abs(r.pos), 0) / n,
    endPos: rows[n - 1].pos,
    priceMove: rows[n - 1].c - rows[0].pc,
    ...underwater(rows),
  };
}

// 언더워터: 누적손익이 직전 고점 아래에 있는 구간. 고점일 다음날부터 고점을 회복한 날까지(영업일 수).
function underwater(rows: Row[]) {
  let peakIdx = -1; // -1 = 시작 시점(누적 0)
  let best = { days: 0, from: null as string | null, to: null as string | null };
  let uwDays = 0;
  for (let k = 0; k < rows.length; k++) {
    if (rows[k].dd >= 0) {
      if (uwDays > best.days) best = { days: uwDays, from: peakIdx >= 0 ? rows[peakIdx].d : rows[0].d, to: rows[k].d };
      peakIdx = k;
      uwDays = 0;
    } else uwDays++;
  }
  const ongoing = uwDays > best.days;
  if (ongoing) best = { days: uwDays, from: peakIdx >= 0 ? rows[peakIdx].d : rows[0].d, to: null };
  const uwShare = rows.filter((r) => r.dd < 0).length / rows.length;
  return { uwMaxDays: best.days, uwMaxFrom: best.from, uwMaxTo: best.to, uwNowDays: uwDays, uwShare };
}

function byYear(rows: Row[]) {
  const m = new Map<string, Row[]>();
  for (const r of rows) {
    const y = r.d.slice(0, 4);
    if (!m.has(y)) m.set(y, []);
    m.get(y)!.push(r);
  }
  return [...m].map(([y, rs]) => {
    let cum = 0, peak = 0, mdd = 0;
    for (const r of rs) { cum += r.pnl; peak = Math.max(peak, cum); mdd = Math.min(mdd, cum - peak); }
    return {
      y, pnl: cum, mdd,
      buyDays: rs.filter((r) => r.buys > 0).length,
      sellDays: rs.filter((r) => r.sells > 0).length,
      tpDays: rs.filter((r) => r.executions.some((e) => e.reason === "tp")).length,
      maxAbs: Math.max(...rs.map((r) => Math.abs(r.pos))),
      move: rs[rs.length - 1].c - rs[0].pc,
    };
  });
}
